import { useMemo } from "react";
import {
  adminNavigation,
  mainNavigation,
  getCategoriesNavigation,
} from "@/app/layouts/navigation";
import { useCategoriesNavigation } from "@/features/categories/hooks/useCategoriesNavigation";
import { cn } from "@/shared/lib/cn";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarRailToggle,
} from "@/shared/ui/nav/sidebar";
import { ChevronLeft, X } from "lucide-react";
import { AppNavigationRailBrand } from "@/app/layouts/AppNavigationRailBrand";
import { NavigationSection } from "@/app/layouts/AppNavigationRailSections";
import { HomeMenuItem } from "./HomeMenuItem";

export interface AppNavigationRailProps {
  collapsed: boolean;
  mobileOpen?: boolean;
  isAdminMode?: boolean;
  isAdminAllowed?: boolean;
  canViewCategories?: boolean;
  onNavigate?: () => void;
  onToggleCollapse: () => void;
  onCloseMobile?: () => void;
}

export function AppNavigationRail({
  collapsed,
  mobileOpen = false,
  isAdminMode = false,
  isAdminAllowed = false,
  canViewCategories = true,
  onNavigate,
  onToggleCollapse,
  onCloseMobile,
}: AppNavigationRailProps) {
  const { data: categories = [], isLoading: categoriesLoading } =
    useCategoriesNavigation();

  const categoriesNavigation = useMemo(
    () => (categories.length > 0 ? getCategoriesNavigation(categories) : []),
    [categories]
  );

  return (
    <Sidebar
      collapsed={collapsed}
      mobileOpen={mobileOpen}
      aria-label="Primary navigation"
      className="md:shadow-[0_0_48px_-24px_rgba(0,59,153,0.45)]"
    >
      <SidebarHeader className="h-auto py-0">
        <AppNavigationRailBrand collapsed={collapsed} />

        <button
          type="button"
          onClick={onCloseMobile}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/60 shadow-sm transition hover:border-white/25 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imperial-500 md:hidden"
          aria-label="Close navigation"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </SidebarHeader>

      <SidebarContent>
        {isAdminMode && isAdminAllowed ? (
          <>
            <div className="space-y-2">
              <HomeMenuItem collapsed={collapsed} onNavigate={onNavigate} />
            </div>
            <NavigationSection
              title="Admin"
              items={adminNavigation}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          </>
        ) : (
          <>
            <NavigationSection
              title=""
              items={mainNavigation}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
            {canViewCategories &&
              !categoriesLoading &&
              categoriesNavigation.length > 0 && (
                <NavigationSection
                  title="Categories"
                  items={categoriesNavigation}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              )}
          </>
        )}
      </SidebarContent>

      <SidebarRailToggle
        collapsed={collapsed}
        onClick={onToggleCollapse}
        aria-label={
          collapsed ? "Expand navigation rail" : "Collapse navigation rail"
        }
      >
        <ChevronLeft
          aria-hidden="true"
          className={cn(
            "h-4 w-4 transition-transform duration-300",
            collapsed && "rotate-180"
          )}
        />
      </SidebarRailToggle>
    </Sidebar>
  );
}
